import type { HTMLAttributes, ReactNode } from 'react';

type Kind = 'default' | 'info' | 'warn' | 'error' | 'accent' | 'invite';

export interface BannerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  kind?: Kind;
  title?: ReactNode;
  children: ReactNode;
}

/** Invite-variant-specific slots */
export interface BannerInviteProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Leading 36×36 mark (icon / monogram). Rendered inside the accent-bordered box. */
  mark?: ReactNode;
  /** Title text for the invite row */
  title?: ReactNode;
  /** Mono meta line (e.g. "from John Doe · Acme Corp") */
  meta?: ReactNode;
  /** Action buttons — typically <Button variant="primary"> + <Button> */
  actions?: ReactNode;
  className?: string;
}

export function Banner({
  kind = 'default',
  title,
  className,
  children,
  ...rest
}: BannerProps) {
  const cls = ['pn-banner', `pn-banner-${kind}`, className].filter(Boolean).join(' ');
  return (
    <div
      className={cls}
      role={kind === 'error' ? 'alert' : kind === 'warn' ? 'status' : undefined}
      {...rest}
    >
      {title ? <strong className="pn-banner-title">{title}</strong> : null}
      <span className="pn-banner-body">{children}</span>
    </div>
  );
}

/** Horizontal notice bar — title + body copy + trailing actions, vertically centered. */
export interface BannerBarProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  kind?: Exclude<Kind, 'invite'>;
  title?: ReactNode;
  /** Trailing action buttons — typically `<Button size="sm">` children. */
  actions?: ReactNode;
  children: ReactNode;
}

export function BannerBar({
  kind = 'default',
  title,
  actions,
  className,
  children,
  ...rest
}: BannerBarProps) {
  const cls = ['pn-banner', 'pn-banner-bar', `pn-banner-${kind}`, className]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      className={cls}
      role={kind === 'error' ? 'alert' : kind === 'warn' ? 'status' : undefined}
      {...rest}
    >
      <div className="pn-banner-bar-content">
        {title ? <strong className="pn-banner-title">{title}</strong> : null}
        <span className="pn-banner-bar-desc">{children}</span>
      </div>
      {actions ? <div className="pn-banner-bar-actions">{actions}</div> : null}
    </div>
  );
}

/**
 * Banner variant for pending-invitation strips.
 * Matches dashboard.html `.invite-strip` — three-column grid:
 *   [mark] [title + meta text] [actions]
 */
export function BannerInvite({
  mark,
  title,
  meta,
  actions,
  className,
  ...rest
}: BannerInviteProps) {
  const cls = ['pn-banner', 'pn-banner-invite', className].filter(Boolean).join(' ');
  return (
    <div className={cls} role="region" {...rest}>
      {mark != null ? (
        <span className="pn-banner-invite-mark" aria-hidden>{mark}</span>
      ) : null}
      <div className="pn-banner-invite-text">
        {title ? <div className="pn-banner-invite-title">{title}</div> : null}
        {meta ? <div className="pn-banner-invite-meta">{meta}</div> : null}
      </div>
      {actions ? (
        <div className="pn-banner-invite-actions">{actions}</div>
      ) : null}
    </div>
  );
}
